// React Router v7 data router — DESIGN/PROJECT-pinned framework choice.
//
// Loaders gate routes by role. Component-side `useMe()` hooks are for
// rendering identity, not authz — the loader is the actual gate.

import { createBrowserRouter } from 'react-router';
import { redirectLoader, requireRoles } from './lib/auth.ts';
import { RootErrorBoundary } from './components/RootErrorBoundary.tsx';
import { Landing } from './routes/Landing.tsx';
import { Login, loginLoader } from './routes/Login.tsx';
import { NotFound } from './routes/NotFound.tsx';
import {
  PortalShell,
  PortalHome,
  PortalCheckIns,
  PortalLeave,
  PortalPreferences,
  PortalMaeve,
  PortalResources,
  PortalPrivacy,
  PortalMore,
} from './routes/Portal.tsx';
import { SignIn, signInLoader } from './routes/SignIn.tsx';
import { SignUp, signUpLoader } from './routes/SignUp.tsx';
import { HrShell } from './routes/HrShell.tsx';
import { HrMembers } from './routes/HrMembers.tsx';
import { HrCases } from './routes/HrCases.tsx';
import { HrCaseDetail } from './routes/HrCaseDetail.tsx';
import { HrNewCase } from './routes/HrNewCase.tsx';
import { HrPolicies } from './routes/HrPolicies.tsx';
import { HrResources } from './routes/HrResources.tsx';
import { HrPolicyAudit } from './routes/HrPolicyAudit.tsx';
import { HrPolicyExtraction } from './routes/HrPolicyExtraction.tsx';
import { HrIntegrations } from './routes/HrIntegrations.tsx';
import { HrAskMaeve } from './routes/HrAskMaeve.tsx';
import { HrSettings } from './routes/HrSettings.tsx';
import { HrOverview } from './routes/HrOverview.tsx';
import { HrMore } from './routes/HrMore.tsx';
import { ManagerShell } from './routes/ManagerShell.tsx';
import { ManagerDashboard } from './routes/ManagerDashboard.tsx';
import { ManagerNewCase } from './routes/ManagerNewCase.tsx';
import { ManagerCaseDetail } from './routes/ManagerCaseDetail.tsx';
import { ManagerFirstResponse } from './routes/ManagerFirstResponse.tsx';
import { ManagerCheckIn } from './routes/ManagerCheckIn.tsx';
import { ManagerCasePolicy } from './routes/ManagerCasePolicy.tsx';
import { ManagerAskMaeve } from './routes/ManagerAskMaeve.tsx';
import { ManagerSettings } from './routes/ManagerSettings.tsx';
import { ManagerResources } from './routes/ManagerResources.tsx';
import { ManagerPolicies } from './routes/ManagerPolicies.tsx';
import { ManagerWelcome } from './routes/ManagerWelcome.tsx';
import { ManagerTeam } from './routes/ManagerTeam.tsx';
import { ManagerDraftResponse } from './routes/ManagerDraftResponse.tsx';
import { ManagerBereavementGuide } from './routes/ManagerBereavementGuide.tsx';
import { AdminSignIn, adminSignInLoader } from './routes/AdminSignIn.tsx';
import { AdminTotp } from './routes/AdminTotp.tsx';
import { AdminVerify } from './routes/AdminVerify.tsx';
import { AdminShell } from './routes/AdminShell.tsx';
import { AdminOverview } from './routes/AdminOverview.tsx';
import { AdminOrgs } from './routes/AdminOrgs.tsx';
import { AdminAnalytics } from './routes/AdminAnalytics.tsx';
import { AdminOrgDetail } from './routes/AdminOrgDetail.tsx';
import { AdminAdmins } from './routes/AdminAdmins.tsx';
import { AdminCgcMembers } from './routes/AdminCgcMembers.tsx';
import { AdminAuditLog } from './routes/AdminAuditLog.tsx';
import { AdminPrompts } from './routes/AdminPrompts.tsx';
import { AdminGovernance } from './routes/AdminGovernance.tsx';
import { AdminQaRedaction } from './routes/AdminQaRedaction.tsx';
import { CgcSignIn, cgcSignInLoader } from './routes/CgcSignIn.tsx';
import { CgcTotp } from './routes/CgcTotp.tsx';
import { CgcVerify } from './routes/CgcVerify.tsx';
import { CgcDashboard } from './routes/CgcDashboard.tsx';
import { Verify } from './routes/Verify.tsx';

const HR_ROLES = ['HR_Admin', 'HR_Viewer'] as const;
const MANAGER_ROLES = ['Manager'] as const;
const ADMIN_ROLES = ['BesideU_Admin'] as const;
const CGC_ROLES = ['CGC_Member'] as const;

export const router = createBrowserRouter([
  {
    // Top-level error boundary — wraps every route so a single page
    // crash doesn't dump the Vite dev overlay or white-screen the
    // user. See RootErrorBoundary for the copy + recovery CTAs.
    errorElement: <RootErrorBoundary />,
    children: [
      { path: '/', element: <Landing /> },
      // Discovery hub that fans out to the four auth surfaces (tenant sign-in,
      // HR self-serve signup, BesideU Admin, CGC member). Direct links to each
      // surface remain canonical — this page is purely a fan-out for visitors
      // who don't know which entry point they want.
      { path: '/login', loader: loginLoader, element: <Login /> },
      { path: '/sign-in', loader: signInLoader, element: <SignIn /> },
      { path: '/sign-up', loader: signUpLoader, element: <SignUp /> },
      // Magic-link interstitial. The email link lands here; a human click POSTs
      // /auth/verify so passive URL prefetchers can't consume the single-use
      // token. Same rationale for /admin/verify and /cgc/verify below.
      { path: '/verify', element: <Verify /> },
      {
        id: 'hr-shell',
        path: '/hr',
        loader: requireRoles(HR_ROLES),
        element: <HrShell />,
        children: [
          // `HrOverview` picks the dense desktop dashboard or the compact
          // phone summary — deliberately different views, see its header.
          { index: true, element: <HrOverview /> },
          { path: 'cases', element: <HrCases /> },
          { path: 'cases/new', element: <HrNewCase /> },
          { path: 'cases/:id', element: <HrCaseDetail /> },
          { path: 'members', element: <HrMembers /> },
          { path: 'policies', element: <HrPolicies /> },
          // §15 — HR manages org-specific support resources (the API is
          // HR_Admin-gated; HR_Viewer gets a read-only list).
          { path: 'resources', element: <HrResources /> },
          // Slice 19 — Multi-Agent Policy Audit detail view (DESIGN §5.4).
          { path: 'policies/:id/audit', element: <HrPolicyAudit /> },
          { path: 'policies/:id/extraction', element: <HrPolicyExtraction /> },
          // Slice 21 — workspace integrations (Slack first, DESIGN §7.2).
          { path: 'integrations', element: <HrIntegrations /> },
          // Slice 11 — HR Maeve mode frontend (DESIGN §5.6.1, §10.1.1).
          // Backend gate is `requireHr + requireMaeveMode('HR_Maeve')`; the
          // route-level loader above (`requireRoles(HR_ROLES)`) is the
          // frontend equivalent.
          { path: 'ask-maeve', element: <HrAskMaeve /> },
          // Org profile (name/headcount/contacts) + read-only privacy config.
          { path: 'settings', element: <HrSettings /> },
          // Below `md` only — the desktop sidebar carries these directly.
          // Holds the six nav items without a tab, plus sign-out.
          { path: 'more', element: <HrMore /> },
          // Retired `/hr/mobile/*` paths — same treatment and reasoning as the
          // Manager and Employee ones.
          { path: 'mobile', loader: redirectLoader('/hr'), element: <></> },
          { path: 'mobile/ask-maeve', loader: redirectLoader('/hr/ask-maeve'), element: <></> },
          { path: 'mobile/cases', loader: redirectLoader('/hr/cases'), element: <></> },
          { path: 'mobile/more', loader: redirectLoader('/hr/more'), element: <></> },
        ],
      },
      // The ONE Manager route tree (PO 2026-07-31). `ManagerShell` picks the
      // bottom-nav app shell or the desktop sidebar/top-nav by VIEWPORT, so
      // every page below is reachable at every width and there is no longer a
      // parallel `/manager/mobile/*` tree behind a User-Agent gate (those
      // paths survive as redirects further down).
      {
        id: 'manager-shell',
        path: '/manager',
        loader: requireRoles(MANAGER_ROLES),
        element: <ManagerShell />,
        children: [
          { index: true, element: <ManagerDashboard /> },
          // Frontdoor into Manager Sub-mode B guidance — a case picker in
          // front of the existing case-scoped guidance endpoint (see
          // ManagerAskMaeve.tsx for why there's no case-less Maeve mode).
          // Renders the full-bleed conversational chat below `md`, the card
          // layout above it.
          { path: 'maeve', element: <ManagerAskMaeve /> },
          // Bottom-nav "Team" tab — and, since `/manager` became the four
          // action cards at every width (PO 2026-08-06), the ONLY pathway
          // list: it carries the search/filter/pager that used to live on
          // the desktop dashboard.
          { path: 'team', element: <ManagerTeam /> },
          // "Help me draft a response" (PO 2026-08-06) — the CASE-LESS
          // drafter. Deliberately not under `cases/:id`: the whole point is
          // that it runs before any pathway exists. Managers who do have one
          // are offered it on the page and hand off to the pathway-scoped
          // `cases/:id/first-response` below.
          { path: 'draft', element: <ManagerDraftResponse /> },
          // "Guide me through a bereavement experience" (PO 2026-08-06) —
          // static advice, no loader, no query, no Maeve call.
          { path: 'guide', element: <ManagerBereavementGuide /> },
          { path: 'cases/new', element: <ManagerNewCase /> },
          // Journey 01 — the focused, PATHWAY-SCOPED first-response
          // destination. No longer the home page's "Help me draft a response"
          // target (PO 2026-08-06 made that card the case-less
          // `/manager/draft` above); it is reached from that drafter's "is
          // this about someone you already support?" picker, from the pathway
          // detail page, and from email deep-links. No device guard: it
          // renders on mobile and desktop (single-column, mobile-first).
          { path: 'cases/:id/first-response', element: <ManagerFirstResponse /> },
          // Journey 04 — the focused check-in destination (reminder deep-link).
          { path: 'cases/:id/check-in', element: <ManagerCheckIn /> },
          // Journey 00 — read-only leave/pay entitlements for THIS pathway.
          // The home page's "Show me the policy" card points at the org-wide
          // `/manager/policies` since PO 2026-08-06 (as a home card this
          // answered "what is whichever pathway is most recent entitled to",
          // and had no target at all before the first one existed); this route
          // is still the right destination from inside a pathway.
          { path: 'cases/:id/policy', element: <ManagerCasePolicy /> },
          // PO Spec Phase 4 §1 (2026-06-21) put the RTW plan form — the
          // priority-1 mobile module — on a separate phone-shaped route at
          // `/manager/mobile/cases/:id`. That shell is gone: this page now
          // renders inside the app shell automatically below `md`, so one
          // route serves both widths and the RTW form is a tap away on a
          // phone without a second component to keep in sync.
          { path: 'cases/:id', element: <ManagerCaseDetail /> },
          // Manager notification-channel preference (PROJECT.md Manager-portal
          // gap #1).
          { path: 'settings', element: <ManagerSettings /> },
          // Support resources catalog (PROJECT.md Manager-portal gap #5).
          { path: 'resources', element: <ManagerResources /> },
          // Manager Policies tab (PO 2026-08-06) — read-only mirror of HR's
          // Policies list, Approved-only.
          { path: 'policies', element: <ManagerPolicies /> },
          // PROJECT.md Manager-portal gap #2 — the one-time first-sign-in
          // welcome walkthrough, also revisitable from `/manager/settings`.
          // INSIDE the shell (PO 2026-08-06): it shipped as a chrome-free
          // top-level route, which the responsive conversion then left behind
          // — every other `/manager/*` page picks its chrome by viewport, so
          // this was the only one that rendered a bare narrow column on a
          // desktop, i.e. "the mobile version". Nesting it is the whole fix;
          // `ManagerShell` gives it the sidebar at `md`+ and the bottom tabs
          // below, and a first-time Manager now sees the nav they are about
          // to use behind the walkthrough rather than a nav-less page.
          { path: 'welcome', element: <ManagerWelcome /> },
          // Retired `/manager/mobile/*` paths (see `redirectLoader`). They sit
          // inside this shell's children so an old bookmark inherits the same
          // `requireRoles` gate as the route it forwards to — NOT because the
          // parent loader is guaranteed to resolve first. React Router runs
          // every matched route's loader in PARALLEL and takes the deepest
          // redirect, so an unauthenticated hit here forwards to `/manager/…`
          // and only then bounces to `/sign-in`. One extra hop, same end
          // state, and nothing protected renders in between — but don't build
          // anything on an assumed parent-then-child ordering.
          //
          // `element` is required even though these never render: the loader
          // always throws, but React Router still warns in dev about a matched
          // leaf route with no element. `<></>` is the cheapest way to keep the
          // console clean without implying there's a page here.
          { path: 'mobile', loader: redirectLoader('/manager'), element: <></> },
          { path: 'mobile/maeve', loader: redirectLoader('/manager/maeve'), element: <></> },
          { path: 'mobile/team', loader: redirectLoader('/manager/team'), element: <></> },
          {
            path: 'mobile/resources',
            loader: redirectLoader('/manager/resources'),
            element: <></>,
          },
          {
            path: 'mobile/cases/:id',
            loader: redirectLoader((args) => `/manager/cases/${String(args.params.id ?? '')}`),
            element: <></>,
          },
        ],
      },
      // The ONE Employee route tree (PO 2026-07-31), mirroring the Manager
      // conversion. `PortalShell` picks the dark section rail or the bottom-nav
      // app shell by VIEWPORT, so every section below is reachable at every
      // width; the UA-gated `/portal/mobile/*` tree it replaced is now
      // redirects. See Portal.tsx for what the three former portals each did.
      {
        id: 'employee-portal',
        path: '/portal',
        loader: requireRoles(['Employee']),
        element: <PortalShell />,
        children: [
          { index: true, element: <PortalHome /> },
          { path: 'check-ins', element: <PortalCheckIns /> },
          { path: 'leave', element: <PortalLeave /> },
          { path: 'preferences', element: <PortalPreferences /> },
          // The Companion chat. Its transcript and red-flag session lock live
          // in the query cache (`lib/use-companion-chat.ts`), NOT component
          // state, so this can be a normal `<Outlet>` route — the previous
          // shell had to keep it permanently mounted and CSS-hide it off-tab
          // to stop a tab switch silently clearing the crisis handoff.
          { path: 'maeve', element: <PortalMaeve /> },
          { path: 'resources', element: <PortalResources /> },
          { path: 'privacy', element: <PortalPrivacy /> },
          // Below `lg` only — the rail carries these directly at lg+. Holds
          // the sections the four bottom tabs can't: leave, preferences,
          // privacy, sign-out.
          { path: 'more', element: <PortalMore /> },
          // Retired `/portal/mobile/*` paths — same treatment and the same
          // reasoning as the Manager ones above.
          { path: 'mobile', loader: redirectLoader('/portal'), element: <></> },
          { path: 'mobile/maeve', loader: redirectLoader('/portal/maeve'), element: <></> },
          {
            path: 'mobile/check-ins',
            loader: redirectLoader('/portal/check-ins'),
            element: <></>,
          },
          {
            path: 'mobile/resources',
            loader: redirectLoader('/portal/resources'),
            element: <></>,
          },
        ],
      },
      // BesideU Admin (slice 11 — DESIGN §4.4 / §6.6). `/admin/sign-in` + `/admin/totp`
      // are unauthenticated entry points (the cid + magic-link tokens carry their own
      // ephemeral auth). `/admin` is loader-gated to `BesideU_Admin` only.
      { path: '/admin/sign-in', loader: adminSignInLoader, element: <AdminSignIn /> },
      { path: '/admin/verify', element: <AdminVerify /> },
      { path: '/admin/totp', element: <AdminTotp /> },
      {
        id: 'admin-shell',
        path: '/admin',
        loader: requireRoles(ADMIN_ROLES, '/admin/sign-in'),
        element: <AdminShell />,
        children: [
          { index: true, element: <AdminOverview /> },
          { path: 'orgs', element: <AdminOrgs /> },
          // Cross-tenant bereavement/support-pathway analytics — pooled
          // counts only, see PROJECT.md "Cross-tenant analytics".
          { path: 'analytics', element: <AdminAnalytics /> },
          { path: 'orgs/:id', element: <AdminOrgDetail /> },
          { path: 'cgc-members', element: <AdminCgcMembers /> },
          // Slice 36 — platform-admin roster (invite / suspend other BesideU
          // Admins). Replaces the `wrangler d1 execute` bootstrap as the routine
          // provisioning path.
          { path: 'admins', element: <AdminAdmins /> },
          // Slice 14 — admin prompt drafting (DESIGN §5.5 / §9.3 #11). The CGC
          // approval surface for these drafts lives at /cgc.
          { path: 'prompts', element: <AdminPrompts /> },
          // Slice 31 — Governance tab. Allocations + free-text settings + DPA
          // active-version surface. Authoring of the DPA wording happens in
          // /admin/prompts (mode = DPA); this tab links there from the DPA panel.
          { path: 'governance', element: <AdminGovernance /> },
          // QA sampler redactor slice — admin spot-check queue for auto-redacted
          // Companion session snapshots. Reached via a link from the Governance
          // tab; not a top-level nav entry because the queue is empty until
          // QA_Redactor + QA_OPT_IN_USER_VISIBLE both go live.
          { path: 'qa-redaction', element: <AdminQaRedaction /> },
          { path: 'audit-log', element: <AdminAuditLog /> },
        ],
      },
      // CGC member sign-in (Slice 12 — DESIGN §5.5 / §9.3). External clinical /
      // legal advisers approve `system_prompts` here. `/cgc/sign-in` + `/cgc/totp`
      // are unauthenticated entry points; `/cgc` is loader-gated to `CGC_Member`.
      { path: '/cgc/sign-in', loader: cgcSignInLoader, element: <CgcSignIn /> },
      { path: '/cgc/verify', element: <CgcVerify /> },
      { path: '/cgc/totp', element: <CgcTotp /> },
      {
        id: 'cgc-shell',
        path: '/cgc',
        loader: requireRoles(CGC_ROLES),
        element: <CgcDashboard />,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
